import {DEFAULT_DESCRIPTION, OG_DEFAULT_DESCRIPTION, OG_DEFAULT_IMAGE} from "../../../pages/config/settings";
import {stripTreeText, truncateTree} from "../../../utility/componentUtil";
import {generateNextImageUrl} from "../../../utility/navigationUtil";
import {RichTextElement} from "../../fundamentals/CCORichText/CCORichTextHelper";
import {getPictureUri} from "../../fundamentals/CCOPicture";
import {isCdnEnabled} from "../../../utility/cdnPictureUtil";

const MAX_DESCRIPTION_LENGTH = 150;
const OG_IMAGE_SETTINGS = {
    ratio: 'portrait_ratio3x4',
    width: 780
}

const truncateDescription = (description: string | RichTextElement, maxLength: number) => {
    if (typeof description === 'object') {
        return truncateTree(description, maxLength);

    } else if (typeof description === 'string') {
        return description.length > maxLength ? description.substring(0, maxLength) + "..." :
            description;
    }
}

const generateOgImageUrl = (url: string) => generateNextImageUrl("/media" + url,
    OG_IMAGE_SETTINGS.ratio,
    OG_IMAGE_SETTINGS.width);

/**
 * Same as generateOgImageUrl, but for an actual CMPicture (as opposed to a plain, already-CoreMedia
 * "ogimage" settings URL) - resolves via getPictureUri so a Celum-CDN-backed picture yields the CDN
 * URL instead of always falling back to the CoreMedia CAE media path. og:image requires an absolute
 * URL: a CDN url is already absolute, the CAE fallback (like generateOgImageUrl above) needs the
 * site origin prepended.
 */
const generateOgImageUrlFromPicture = (picture: any, isPreview: boolean): string | undefined => {
    const uri = getPictureUri(String(OG_IMAGE_SETTINGS.width), isPreview, picture, OG_IMAGE_SETTINGS.ratio);
    if (!uri) {
        return undefined;
    }
    if (isCdnEnabled(picture)) {
        return uri;
    }
    const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : '';
    return origin + uri;
}

const getOgInfoFromContent = (content: any, type: 'ogdescription' | 'ogtitle' | 'ogimage') => {
    const contentSettings = content?.settings ||
        content?.publicationSettings ||
        content?.eventSettings ||
        content?.newsSettings;
    return contentSettings?.[type] || null;
}

export const getTitle = (document: any) => {
    const htmlTitle = document.htmlTitle;
    const title = document.title;
    return htmlTitle || title;
}

export const getCompleteDescription = (document: any, globalSettings: any) => {
    const htmlDescription = document.htmlDescription;
    const teaserText = document.teaserText?.textAsTree;
    const descriptionText = htmlDescription || teaserText || globalSettings[DEFAULT_DESCRIPTION];
    if (descriptionText) {
        if (typeof descriptionText === 'string') {
            return descriptionText.trim();
        } else {
            return stripTreeText(descriptionText).trim();
        }
    } else {
        return descriptionText;
    }
}

export const getDescription = (document: any, globalSettings: any) => {
    const htmlDescription = document.htmlDescription;
    const teaserText = document.teaserText?.textAsTree;
    const descriptionText = htmlDescription || teaserText || globalSettings[DEFAULT_DESCRIPTION];
    return truncateDescription(descriptionText, MAX_DESCRIPTION_LENGTH);
}

export const getOgTitle = (page: any, specialContent: any, isPagelikeContent: boolean) => {
    if (isPagelikeContent) {
        const contentOgTitle = getOgInfoFromContent(specialContent, 'ogtitle');
        const contentTitle = specialContent?.title;
        return contentOgTitle || contentTitle;
    } else {
        const pageOgTitle = page.settings?.ogtitle;
        const pageTitle = page.title;
        return pageOgTitle || pageTitle;
    }
}

export const getOgDescription = (page: any, specialContent: any, globalSettings: any,
                                 isPagelikeContent: boolean) => {
    let ogDescriptionText = '';
    if (isPagelikeContent) {
        const contentOgDescription = getOgInfoFromContent(specialContent, 'ogdescription');
        const contentTeaserText = specialContent?.teaserText?.textAsTree;
        ogDescriptionText =
            contentOgDescription || contentTeaserText || globalSettings[OG_DEFAULT_DESCRIPTION];
    } else {
        const pageOgDescription = page.settings?.ogdescription;
        const pageTeaserText = page.teaserText?.textAsTree;
        ogDescriptionText =
            pageOgDescription || pageTeaserText || globalSettings[OG_DEFAULT_DESCRIPTION];
    }
    return truncateDescription(ogDescriptionText, MAX_DESCRIPTION_LENGTH);
}


export const getOgImage = (page: any, specialContent: any, globalSettings: any,
                           isPagelikeContent: boolean, isPreview: boolean = false) => {
    if (!specialContent) {
        if (page.settings?.ogimage) {
            return generateOgImageUrl(page.settings?.ogimage);
        } else if (page.picture?.data?.uri) {
            return generateOgImageUrlFromPicture(page.picture, isPreview) ??
                generateOgImageUrl(page.picture.data.uri)
        }
    }
    if (isPagelikeContent) {
        const contentOgImage = getOgInfoFromContent(specialContent, 'ogimage');
        if (contentOgImage) {
            return generateOgImageUrl(contentOgImage)
        } else if (specialContent.picture?.data?.uri) {
            return generateOgImageUrlFromPicture(specialContent.picture, isPreview) ??
                generateOgImageUrl(specialContent.picture.data.uri)
        }
    }
    return generateOgImageUrl(globalSettings[OG_DEFAULT_IMAGE]);
}

export const getRobotsContent = (document: any) => document.robots ? "noindex, follow" :
    "index, follow";

export const normalizeCanonicalUrl = (url: string): string => {
    try {
        const parsed = new URL(url);
        parsed.pathname = parsed.pathname
            .split('/')
            .map(segment => segment.replace(/-{2,}/g, '-'))
            .join('/');
        return parsed.toString();
    } catch {
        return url;
    }
}

export const getCanonicalUrl = (document: any, currentUrl: string) => {
    try {
        const canonicalUrl = document.canonicalLink?.[0]?.canonicalUrl || document.canonicalUrl;
        const canonicalDomain = new URL(canonicalUrl).hostname;
        const currentDomain = currentUrl && new URL(currentUrl).hostname;
        const resolvedUrl = canonicalDomain === currentDomain
            ? canonicalUrl
            : canonicalUrl.replace(canonicalDomain, currentDomain);
        return normalizeCanonicalUrl(resolvedUrl);
    } catch {
        return normalizeCanonicalUrl(currentUrl);
    }
}
